/********************************************************************
 *  Copyright 2025 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  Adobe permits you to use, modify, and distribute this
 * file in accordance with the terms of the Adobe license agreement
 * accompanying it.
 *******************************************************************/

import { CartItemModel } from '../models/negotiable-quote-model';
import { state } from '@/quote-management/lib/state';

/**
 * Finds the most applicable price tier for a given quantity
 * @param priceTiers - Array of price tier objects
 * @param quantity - Current item quantity
 * @returns The applicable tier or null if none found
 */
function findApplicablePriceTier(priceTiers: any[], quantity: number) {
  if (!priceTiers?.length) return null;

  // Sort price tiers by quantity in descending order to find the highest applicable tier
  const sortedTiers = [...priceTiers].sort(
    (a: any, b: any) => b.quantity - a.quantity
  );

  // Find the highest tier where the current quantity meets or exceeds the tier's minimum quantity
  return sortedTiers.find((tier: any) => quantity >= tier.quantity) || null;
}

/**
 * Helper function to get the image for a cart item component
 * @param item - Cart item from API
 * @returns Image object with src and alt
 */
function getImage(item: any) {
  const config = state.config;

  return {
    // Use parent thumbnail if configured, otherwise use own variant. Use the parent thumbnail as a fallback
    src: config?.useConfigurableParentThumbnail
      ? item.product.thumbnail.url
      : item.configured_variant?.thumbnail?.url || item.product.thumbnail.url,
    alt: config?.useConfigurableParentThumbnail
      ? item.product.thumbnail.label
      : item.configured_variant?.thumbnail?.label ||
        item.product.thumbnail.label,
  };
}

/**
 * Helper function to get downloadable links for a cart item component
 * @param item - Cart item from API
 * @returns Links object with count and result, or null if no links
 */
function getLinks(item: any) {
  return item.links?.length > 0
    ? {
        count: item.links.length,
        result: item.links.map((link: any) => link.title).join(', '),
      }
    : null;
}

/**
 * Helper function to check if an item is discounted
 * @param item - Cart item from API
 * @returns True if the item has a discount
 */
function getDiscounted(item: any) {
  const quantity = item.quantity;
  const isConfigurable = item.__typename === 'ConfigurableCartItem';

  // Get price tiers and price range based on item type
  const priceTiers = isConfigurable
    ? item.configured_variant?.price_tiers
    : item.product.price_tiers;
  const priceRange = isConfigurable
    ? item.configured_variant?.price_range
    : item.product.price_range;

  // Check price tiers first for discount eligibility
  const applicableTier = findApplicablePriceTier(priceTiers, quantity);
  if (applicableTier) {
    return applicableTier.discount.amount_off > 0;
  }

  // Fallback to regular price range discount
  return (priceRange?.maximum_price?.discount?.amount_off ?? 0) > 0;
}

/**
 * Helper function to get the discount percentage of an item
 * @param item - Cart item from API
 * @returns Discount percentage rounded to nearest integer, or undefined if no discount
 */
function getDiscountPercentage(item: any) {
  const quantity = item.quantity;

  // Check price tiers first for discount percentage
  const applicableTier = findApplicablePriceTier(
    item.product.price_tiers,
    quantity
  );

  if (applicableTier) {
    return Math.round(applicableTier.discount.percent_off);
  }

  // Fallback to product-specific discount percentage
  let percent_off;

  if (item.__typename === 'ConfigurableCartItem') {
    percent_off =
      item?.configured_variant?.price_range?.maximum_price?.discount
        ?.percent_off;
  } else if (item.__typename === 'BundleCartItem') {
    return undefined;
  } else {
    percent_off =
      item?.product?.price_range?.maximum_price?.discount?.percent_off;
  }

  if (percent_off === 0) {
    return undefined;
  }

  return Math.round(percent_off);
}

/**
 * Helper function to get the regular price of an item
 * @param item - Cart item from API
 * @returns Regular price object with value and currency
 */
function getRegularPrice(item: any) {
  if (item.__typename === 'ConfigurableCartItem') {
    return {
      value:
        item.configured_variant?.price_range?.maximum_price.regular_price.value,
      currency:
        item.configured_variant?.price_range?.maximum_price.regular_price
          .currency,
    };
  }
  return {
    value: item.prices.original_item_price.value,
    currency: item.prices.original_item_price.currency,
  };
}

/**
 * Helper function to get the savings amount of an item
 * @param item - Cart item from API
 * @returns Savings amount object with value and currency, or undefined if no savings
 */
function getSavingsAmount(item: any) {
  const amount_off =
    item?.prices?.original_row_total?.value - item?.prices?.row_total?.value;
  const currency = item?.prices?.row_total?.currency;

  if (amount_off === 0) {
    return undefined;
  }
  return {
    value: amount_off,
    currency,
  };
}

/**
 * Transforms a single quote item from the API format to the model format
 * @param item - Raw quote item from API
 * @returns Transformed quote item
 */
export function transformQuoteItem(item: any): CartItemModel {
  return {
    itemType: item.__typename,
    uid: item.uid,
    product: {
      uid: item.product.uid,
      sku: item.product.sku,
      name: item.product.name,
      priceRange: {
        maximumPrice: {
          regularPrice: {
            value:
              item.product.price_range.maximum_price.regular_price.value,
            currency:
              item.product.price_range.maximum_price.regular_price.currency,
          },
        },
      },
    },
    image: getImage(item),
    links: getLinks(item),
    discounted: getDiscounted(item),
    discountedTotal: {
      value: item.prices.row_total.value,
      currency: item.prices.row_total.currency,
    },
    catalogDiscount: {
      amountOff: item.prices.catalog_discount.amount_off,
      percentOff: item.prices.catalog_discount.percent_off,
    },
    rowCatalogDiscount: {
      amountOff: item.prices.row_catalog_discount.amount_off,
      percentOff: item.prices.row_catalog_discount.percent_off,
    },
    discounts:
      item.prices?.discounts?.map((discount: any) => ({
        label: discount.label,
        value: discount.value,
        amount: {
          value: discount.amount.value,
          currency: discount.amount.currency,
        },
      })) ?? [],
    discountPercentage: getDiscountPercentage(item),
    insufficientQuantity:
      (item.__typename === 'ConfigurableCartItem'
        ? item.configured_variant?.stock_status ?? item.product.stock_status
        : item.product.stock_status
      ) === 'IN_STOCK' && !item.is_available,
    outOfStock: item.product.stock_status === 'OUT_OF_STOCK',
    stockStatus: item.product.stock_status,
    quantity: item.quantity,
    prices: {
      regularPrice: getRegularPrice(item),
      priceIncludingTax: {
        value: item.prices.price_including_tax.value,
        currency: item.prices.price_including_tax.currency,
      },
      originalItemPrice: {
        value: item.prices.original_item_price.value,
        currency: item.prices.original_item_price.currency,
      },
      originalRowTotal: {
        value: item.prices.original_row_total.value,
        currency: item.prices.original_row_total.currency,
      },
      rowTotal: {
        value: item.prices.row_total.value,
        currency: item.prices.row_total.currency,
      },
      rowTotalIncludingTax: {
        value: item.prices.row_total_including_tax.value,
        currency: item.prices.row_total_including_tax.currency,
      },
    },
    savingsAmount: getSavingsAmount(item),
    noteFromBuyer: item.note_from_buyer?.map((note: any) => ({
      createdAt: note.created_at,
      creatorId: note.creator_id,
      creatorType: note.creator_type,
      negotiableQuoteItemUid: note.negotiable_quote_item_uid,
      note: note.note,
      noteUid: note.note_uid,
    })),
    noteFromSeller: item.note_from_seller?.map((note: any) => ({
      createdAt: note.created_at,
      creatorId: note.creator_id,
      creatorType: note.creator_type,
      negotiableQuoteItemUid: note.negotiable_quote_item_uid,
      note: note.note,
      noteUid: note.note_uid,
    })),
    configurableOptions: item.configurable_options?.map((option: any) => ({
      optionLabel: option.option_label,
      valueLabel: option.value_label,
    })),
    bundleOptions: item.bundle_options?.map((option: any) => ({
      label: option.label,
      values: option.values.map((value: any) => ({
        label: value.label,
        quantity: value.quantity,
        originalPrice: {
          value: value.original_price.value,
          currency: value.original_price.currency,
        },
        price: {
          value: value.priceV2.value,
          currency: value.priceV2.currency,
        },
      })),
    })),
    customizableOptions: item.customizable_options?.map((option: any) => ({
      type: option.type,
      label: option.label,
      values: option.values.map((value: any) => ({
        label: value.label,
        value: value.value,
      })),
    })),
  };
}

/**
 * Transforms an array of quote items from the API format to the model format
 * @param items - Array of raw quote items from API
 * @returns Array of transformed quote items
 */
export function transformQuoteItems(
  items: any[] | null | undefined
): CartItemModel[] {
  if (!items) {
    return [];
  }

  return items.map(transformQuoteItem);
}

